import React, { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { Box, Typography, IconButton, TextField, Paper } from '@mui/material';
import FooterActions from '../common/FooterActions';
import storageService from '../../../services/storageService';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

interface KPI {
  id: string;
  description: string;
}

interface KPIsData {
  [key: string]: KPI;
}

interface CustomProcessInfoProps {
  processDescription: string;
  kpis: KPIsData;
  onUpdate: (processDescription: string, kpis: KPIsData) => void;
  onSaveStateChange?: (enabled: boolean) => void;
  files: Record<string, { name: string; url: string }>;
}

const CustomProcessInfo: React.FC<CustomProcessInfoProps> = ({
  processDescription: initialProcessDescription,
  kpis: initialKpis,
  onUpdate,
  files,
}) => {
  const [processDescription, setProcessDescription] = useState(initialProcessDescription);
  const [kpis, setKpis] = useState<KPIsData>(initialKpis || { 1: { id: uuid(), description: '' } });
  const [filesArray, setFilesArray] = useState<{ name: string; url: string }[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset state when props change
  useEffect(() => {
    setProcessDescription(initialProcessDescription);
    setKpis(initialKpis || { 1: { id: uuid(), description: '' } });
    const filesEntries = (Object.values(files || {}) as { name: string; url: string }[]).map(
      (file) => ({
        name: file.name,
        url: file.url,
      })
    );
    setFilesArray(filesEntries);
    setHasChanges(false);
  }, [initialProcessDescription, initialKpis, files]);

  // Track changes
  useEffect(() => {
    const hasDescChanged = processDescription !== initialProcessDescription;
    const kpisStringified = JSON.stringify(kpis);
    const initialKpisStringified = JSON.stringify(
      initialKpis || { 1: { id: uuid(), description: '' } }
    );
    const hasKpisChanged = kpisStringified !== initialKpisStringified;

    setHasChanges(hasDescChanged || hasKpisChanged);
  }, [processDescription, kpis, initialProcessDescription, initialKpis]);

  const handleUpdate = () => {
    onUpdate(processDescription, kpis);
    setHasChanges(false);
  };

  const handleDiscard = () => {
    setProcessDescription(initialProcessDescription);
    setKpis(initialKpis || { 1: { id: uuid(), description: '' } });
    setHasChanges(false);
  };

  const handleKpiChange = (key: string, value: string) => {
    setKpis((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        description: value,
      },
    }));
  };

  const handleAddKpi = (afterKey: string) => {
    setKpis((prevKpis) => {
      const keys = Object.keys(prevKpis)
        .map(Number)
        .sort((a, b) => a - b);
      const afterKeyNum = Number(afterKey);
      const newKpis: KPIsData = {};

      let inserted = false;
      let newKeyCounter = 1;

      for (const key of keys) {
        newKpis[String(newKeyCounter)] = prevKpis[String(key)];
        newKeyCounter++;

        if (key === afterKeyNum && !inserted) {
          newKpis[String(newKeyCounter)] = {
            id: uuid(),
            description: '',
          };
          newKeyCounter++;
          inserted = true;
        }
      }

      return newKpis;
    });
  };

  const handleRemoveKpi = (keyToRemove: string) => {
    if (Object.keys(kpis).length <= 1) {
      return;
    }

    setKpis((prevKpis) => {
      const keys = Object.keys(prevKpis)
        .map(Number)
        .sort((a, b) => a - b);
      const removeKeyNum = Number(keyToRemove);
      const newKpis: KPIsData = {};

      let newKeyCounter = 1;

      for (const key of keys) {
        if (key === removeKeyNum) {
          continue;
        }

        newKpis[String(newKeyCounter)] = prevKpis[String(key)];
        newKeyCounter++;
      }

      return newKpis;
    });
  };

  const kpiEntries = Object.keys(kpis)
    ?.map(Number)
    ?.sort((a, b) => a - b)
    ?.map((key) => [String(key), kpis[String(key)]] as [string, KPI]);

  return (
    <Box
      sx={{
        marginRight: { xs: 0, sm: '20px' },
        width: '100%',
        maxWidth: '1000px',
        height: { xs: '100%' },
        p: 1,
        zIndex: 1299,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        borderRadius: { xs: 0, sm: 2 },
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header */}
      {/* <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '4px',
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
          pb: 2,
        }}
      > */}
      <Typography sx={{ fontWeight: 500, fontSize: '24px', color: 'text.primary', mb: 1 }}>
        Process Information
      </Typography>
      {/* </Box> */}

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, pb: '90px' }}>
        {/* Process Description Section */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 400,
              color: 'text.primary',
              fontSize: '16px',
              mb: 1,
            }}
          >
            Process description
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            value={processDescription}
            onChange={(e) => setProcessDescription(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.default',
                fontSize: '16px',
                fontWeight: 400,
              },
            }}
          />
        </Box>

        {/* KPIs Section */}
        {kpiEntries?.length > 0 && (
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 400,
                color: 'text.primary',
                fontSize: '16px',
              }}
            >
              Key Performance Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {kpiEntries.map(([key, kpi]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 0,
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={6}
                      value={kpi.description}
                      onChange={(e) => handleKpiChange(key, e.target.value)}
                      variant="outlined"
                      placeholder="Enter KPI description"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                          fontSize: '16px',
                          fontWeight: 400,
                        },
                      }}
                    />
                  </Box>
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleAddKpi(key)}
                      sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.lighter' },
                        mt: 0.5,
                      }}
                    >
                      <img src={'/images/add.svg'} alt="add" height={'16px'} width={'16px'} />
                    </IconButton>
                    {kpiEntries.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveKpi(key)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.lighter' },
                          mt: 0.5,
                        }}
                      >
                        <img
                          src={'/images/subtract.svg'}
                          alt="subtract"
                          height={'16px'}
                          width={'16px'}
                        />
                      </IconButton>
                    )}
                  </>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* FILE section */}
        {filesArray?.length > 0 && (
          <Box sx={{ width: '100%', mt: 2, mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: 'text.secondary', fontWeight: 500, fontSize: '16px' }}
            >
              Process Files
            </Typography>

            {filesArray.map((file, index) => (
              <Paper
                key={index}
                elevation={0}
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: 1,
                  height: '44px',
                  px: 1.5,
                  py: 0.5,
                  mb: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <img
                    src="/images/customFiles.png"
                    alt="file"
                    style={{ height: '22px', flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  sx={{ color: '#000000', flexShrink: 0 }}
                  onClick={() => storageService.viewFile(file.url)}
                >
                  <VisibilityOutlinedIcon />
                </IconButton>
              </Paper>
            ))}
          </Box>
        )}

        {/* Footer */}
        <FooterActions
          onSave={hasChanges ? handleUpdate : undefined}
          onDiscard={hasChanges ? handleDiscard : undefined}
          onRegenerate={() => {}}
          saveDisabled={!hasChanges || !kpiEntries?.length}
          regenerateDisabled={true}
        />
      </Box>
    </Box>
  );
};

export default CustomProcessInfo;
